import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { newToastEvent } from "../../../utils/ToastUtil";
import API from "../../api/API";
import { AppConfirm } from "../../../utils/PopupUtil";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

interface MutationProps {
  tenantID: string;
  cloudBackupID: string;
}

const useDeleteCloudBackup = () => {
  const queryClient = useQueryClient();
  const atomTenantID = useAtomValue(tenantIDAtom);

  const confirmDeleteNewestCloudBackup = async () => {
    const result = await AppConfirm({
      title: "Confirm Newest Cloud Backup Deletion",
      text: "Are you sure you want to delete the newest cloud backup?",
      icon: "error",
    });

    return result.isConfirmed;
  };

  const confirmDeleteCloudBackup = async () => {
    const result = await AppConfirm({
      title: "Confirm Cloud Backup Deletion",
      text: "Are you sure you want to delete this cloud backup?",
    });

    return result.isConfirmed;
  };

  const deleteCloudBackup = useMutation({
    mutationFn: async ({ tenantID, cloudBackupID }: MutationProps) => {
      return API.deleteCloudBackup(tenantID, cloudBackupID);
    },
    onSuccess: (data, { tenantID }: MutationProps) => {
      queryClient.invalidateQueries({
        queryKey: [tenantID || atomTenantID, "records", "CloudBackups"],
      });
      newToastEvent("success", data?.data.message);
    },
    onError: (err) => {
      const errorMessage =
        "You don't have permissions to delete a cloud backup.";
      newToastEvent("error", errorMessage);
    },
  });

  return {
    confirmDeleteCloudBackup: confirmDeleteCloudBackup,
    confirmDeleteNewestCloudBackup: confirmDeleteNewestCloudBackup,
    deleteCloudBackup: deleteCloudBackup.mutate,
    deleteCloudBackupLoading: deleteCloudBackup.isPending,
    deleteCloudBackupError: deleteCloudBackup.isError,
  };
};

export default useDeleteCloudBackup;
